import React from 'react';
import { CheckIcon, StarIcon, TrendingUpIcon } from 'lucide-react';
type HeatmapCellProps = {
  value: string;
  qualityScore: number;
  isSelected: boolean;
  isRecommended: boolean;
  isMostFrequent: boolean;
  onClick: () => void;
  formatValue?: (value: string) => string;
  recordLabel: string;
  fieldLabel: string;
};
export function HeatmapCell({
  value,
  qualityScore,
  isSelected,
  isRecommended,
  isMostFrequent,
  onClick,
  formatValue = v => v,
  recordLabel,
  fieldLabel
}: HeatmapCellProps) {
  const isEmpty = !value || value === 'undefined' || value.trim() === '';
  // Color based on quality score
  const getBackgroundColor = () => {
    if (isSelected) {
      return 'bg-blue-600 border-blue-700';
    }
    if (isEmpty) {
      return 'bg-red-100 border-red-200';
    }
    if (qualityScore >= 80) {
      return 'bg-green-100 border-green-300 hover:bg-green-200';
    }
    if (qualityScore >= 50) {
      return 'bg-yellow-100 border-yellow-300 hover:bg-yellow-200';
    }
    return 'bg-orange-100 border-orange-300 hover:bg-orange-200';
  };
  const getTextColor = () => {
    if (isSelected) return 'text-white';
    if (isEmpty) return 'text-red-400';
    return 'text-gray-900';
  };
  return <button onClick={onClick} className={`relative w-full h-20 border-2 rounded-lg transition-all duration-200 p-2 text-left group ${getBackgroundColor()} focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1`} title={`${fieldLabel} from ${recordLabel}: ${isEmpty ? 'Empty' : formatValue(value)} (Quality: ${qualityScore}%)`} aria-label={`Select ${fieldLabel} from ${recordLabel}`} aria-pressed={isSelected} data-id="element-109">
      {/* Quality Score Badge */}
      <div className="absolute top-1 right-1 flex items-center gap-1" data-id="element-110">
        {isRecommended && !isSelected && <div className="bg-blue-500 rounded-full p-0.5" title="Recommended (Golden Path)" data-id="element-111">
            <StarIcon className="w-2.5 h-2.5 text-white fill-white" aria-hidden="true" data-id="element-112" />
          </div>}
        {isMostFrequent && !isEmpty && <div className="bg-purple-500 rounded-full p-0.5" title="Most Frequent Value" data-id="element-113">
            <TrendingUpIcon className="w-2.5 h-2.5 text-white" aria-hidden="true" data-id="element-114" />
          </div>}
        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${isSelected ? 'bg-blue-800 text-white' : isEmpty ? 'bg-red-200 text-red-700' : qualityScore >= 80 ? 'bg-green-200 text-green-800' : qualityScore >= 50 ? 'bg-yellow-200 text-yellow-800' : 'bg-orange-200 text-orange-800'}`} data-id="element-115">
          {qualityScore}
        </span>
      </div>

      {/* Value - Full content visible with wrapping */}
      <div className={`text-base font-semibold break-words pr-12 ${getTextColor()}`} data-id="element-116">
        {isEmpty ? <span className="italic" data-id="element-117">Empty</span> : formatValue(value)}
      </div>

      {/* Selected Indicator */}
      {isSelected && <div className="absolute bottom-1 right-1" data-id="element-118">
          <CheckIcon className="w-4 h-4 text-white" aria-hidden="true" data-id="element-119" />
        </div>}

      {/* Hover Tooltip */}
      <div className="absolute left-0 top-full mt-1 hidden group-hover:block z-50 bg-gray-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap shadow-lg" data-id="element-120">
        <div className="font-semibold" data-id="element-121">{fieldLabel}</div>
        <div className="text-gray-300" data-id="element-122">{recordLabel}</div>
        <div className="mt-1" data-id="element-123">{isEmpty ? 'No value' : formatValue(value)}</div>
        <div className="text-gray-400 mt-0.5" data-id="element-124">Quality: {qualityScore}%</div>
      </div>
    </button>;
}